import { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Search, Plus, ChevronLeft, ChevronRight, Users, Building2, MapPin, Briefcase,
  Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Download, Mail
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import { toast } from 'sonner';

// SortIcon component
const SortIcon = ({ field, sortField, sortOrder }) => {
  if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
  return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
};

const Usuarios = () => {
  // Data states
  const [usuarios, setUsuarios] = useState([]);
  const [stats, setStats] = useState({ total: 0, por_empresa: [], sin_empresa: 0 });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  
  // Filter states
  const [search, setSearch] = useState('');
  const [empresaFilter, setEmpresaFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  
  // Form states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', emailaddress: '', title: '', empresa_id: '', site_id: '', location_id: ''
  });
  const [formOptions, setFormOptions] = useState({ empresas: [], sites: [], ubicaciones: [] });
  const [filterOptions, setFilterOptions] = useState({ empresas: [], sites: [] });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Permissions
  const { canCreate, canUpdate, canDelete } = usePermissions('config', 'users');

  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(usuarios);

  // Sorting
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        page: pagination.page, 
        limit: pagination.limit,
        sort_by: sortField,
        sort_dir: sortOrder
      });
      if (search && search.length >= 2) params.append('search', search);
      if (empresaFilter) params.append('empresa_id', empresaFilter);
      if (siteFilter) params.append('site_id', siteFilter);
      
      const response = await api.get(`/config/usuarios?${params}`);
      setUsuarios(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, empresaFilter, siteFilter, sortField, sortOrder]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/config/usuarios/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/config/usuarios/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/config/usuarios/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFormOptions();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search === '' || search.length >= 2) {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [search]);

  // CRUD handlers
  const handleCreate = () => {
    setEditingItem(null);
    setFormData({ name: '', emailaddress: '', title: '', empresa_id: '', site_id: '', location_id: '' });
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      emailaddress: item.emailaddress || '',
      title: item.title || '',
      empresa_id: item.empresa_id?.toString() || '',
      site_id: item.site_id?.toString() || '',
      location_id: item.location_id?.toString() || ''
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    try {
      const payload = {
        name: formData.name.trim(),
        emailaddress: formData.emailaddress || null,
        title: formData.title || null,
        empresa_id: formData.empresa_id ? parseInt(formData.empresa_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        location_id: formData.location_id ? parseInt(formData.location_id) : null
      };

      if (editingItem) {
        await api.put(`/config/usuarios/${editingItem.id}`, payload);
        toast.success('Usuario actualizado');
      } else {
        await api.post('/config/usuarios', payload);
        toast.success('Usuario creado');
      }
      
      setModalOpen(false);
      fetchData();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/config/usuarios/${id}`);
      toast.success('Usuario eliminado');
      setDeleteConfirm(null);
      fetchData();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (empresaFilter) params.append('empresa_id', empresaFilter);
      if (siteFilter) params.append('site_id', siteFilter);
      
      const response = await api.get(`/config/usuarios/export/excel?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `usuarios_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Archivo exportado');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Usuarios</h1>
            <p className="page-subtitle">Gestión de usuarios para asignación de activos</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport} data-testid="export-btn">
              <Download className="h-4 w-4 mr-2" /> Exportar
            </Button>
            {canCreate && (
              <Button className="btn btn-primary" onClick={handleCreate} data-testid="add-usuario-btn">
                <Plus className="h-4 w-4 mr-2" /> Nuevo Usuario
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stats-card">
            <div className="mb-2"><Users className="h-8 w-8 text-primary" strokeWidth={1.5} /></div>
            <div className="stats-card-value">{stats.total}</div>
            <div className="stats-card-label">Total Usuarios</div>
          </div>
          {stats.por_empresa.slice(0, 2).map((emp, idx) => (
            <div key={idx} className="stats-card">
              <div className="mb-2"><Building2 className="h-8 w-8 text-primary" strokeWidth={1.5} /></div>
              <div className="stats-card-value">{emp.count}</div>
              <div className="stats-card-label truncate" title={emp.nombre || 'Sin Empresa'}>
                {emp.nombre || 'Sin Empresa'}
              </div>
            </div>
          ))}
          <div className="stats-card">
            <div className="mb-2"><Briefcase className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} /></div>
            <div className="stats-card-value">{stats.sin_empresa}</div>
            <div className="stats-card-label">Sin Asignar</div>
          </div>
        </div>

        {/* Table Card */}
        <Card className="card-subtle overflow-hidden">
          <div className="p-4 border-b border-border/30">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, email o cargo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              <Select value={empresaFilter} onValueChange={(v) => { setEmpresaFilter(v === 'all' ? '' : v); setPagination(p => ({...p, page: 1})); }}>
                <SelectTrigger className="w-[180px]" data-testid="empresa-filter">
                  <SelectValue placeholder="Empresa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las empresas</SelectItem>
                  {filterOptions.empresas.map(e => (
                    <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={siteFilter} onValueChange={(v) => { setSiteFilter(v === 'all' ? '' : v); setPagination(p => ({...p, page: 1})); }}>
                <SelectTrigger className="w-[180px]" data-testid="site-filter">
                  <SelectValue placeholder="Site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los sites</SelectItem>
                  {filterOptions.sites.map(s => (
                    <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th className="w-10">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            onCheckedChange={toggleAll}
                            data-testid="select-all-checkbox"
                          />
                        </th>
                        <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('name')}>
                          <div className="flex items-center gap-1">NOMBRE <SortIcon field="name" sortField={sortField} sortOrder={sortOrder} /></div>
                        </th>
                        <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('emailaddress')}>
                          <div className="flex items-center gap-1">EMAIL <SortIcon field="emailaddress" sortField={sortField} sortOrder={sortOrder} /></div>
                        </th>
                        <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('title')}>
                          <div className="flex items-center gap-1">CARGO <SortIcon field="title" sortField={sortField} sortOrder={sortOrder} /></div>
                        </th>
                        <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('empresa')}>
                          <div className="flex items-center gap-1">EMPRESA <SortIcon field="empresa" sortField={sortField} sortOrder={sortOrder} /></div>
                        </th>
                        <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('site')}>
                          <div className="flex items-center gap-1">SITE <SortIcon field="site" sortField={sortField} sortOrder={sortOrder} /></div>
                        </th>
                        <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('ubicacion')}>
                          <div className="flex items-center gap-1">ÁREA <SortIcon field="ubicacion" sortField={sortField} sortOrder={sortOrder} /></div>
                        </th>
                        <th className="w-24 text-center">ACCIONES</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuarios.length === 0 ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="empty-state py-12">
                              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron usuarios</p>
                            </div>
                          </td>
                        </tr>
                      ) : usuarios.map((user) => (
                        <tr key={user.id} data-testid={`user-row-${user.id}`} className={isSelected(user.id) ? 'bg-primary/5' : ''}>
                          <td>
                            <Checkbox
                              checked={isSelected(user.id)}
                              onCheckedChange={() => toggleRow(user.id)}
                              data-testid={`select-row-${user.id}`}
                            />
                          </td>
                          <td className="font-medium">{user.name}</td>
                          <td>
                            {user.emailaddress ? (
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Mail className="h-3 w-3" />
                                {user.emailaddress}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="text-muted-foreground">{user.title || '-'}</td>
                          <td>
                            {user.empresa_nombre ? (
                              <span className="badge badge-info">{user.empresa_nombre}</span>
                            ) : <span className="text-muted-foreground">-</span>}
                          </td>
                          <td className="text-muted-foreground">{user.site_nombre || '-'}</td>
                          <td className="text-muted-foreground">{user.ubicacion_nombre || '-'}</td>
                          <td className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {canUpdate && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleEdit(user)}
                                  data-testid={`edit-btn-${user.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => setDeleteConfirm(user)}
                                  data-testid={`delete-btn-${user.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.pages > 0 && (
                  <div className="flex items-center justify-between p-4 border-t border-border/30">
                    <div className="text-sm text-muted-foreground">
                      {pagination.total > 0
                        ? `${((pagination.page - 1) * pagination.limit) + 1} - ${Math.min(pagination.page * pagination.limit, pagination.total)} de ${pagination.total}`
                        : '0 registros'}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                        disabled={pagination.page === 1}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        {pagination.page} / {pagination.pages || 1}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                        disabled={pagination.page >= pagination.pages}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Bulk Edit Bar */}
        {canUpdate && (
          <BulkEditBar
            selectedIds={selectedIds}
            onClear={clearSelection}
            onSuccess={() => { fetchData(); fetchStats(); fetchFilterOptions(); }}
            endpoint="/config/usuarios"
            options={{
              empresas: formOptions.empresas,
              sites: formOptions.sites,
              ubicaciones: formOptions.ubicaciones
            }}
            fields={['empresa_id', 'site_id', 'location_id']}
          />
        )}

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nombre completo"
                  data-testid="input-name"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.emailaddress}
                  onChange={(e) => setFormData(prev => ({ ...prev, emailaddress: e.target.value }))}
                  placeholder="correo@empresa.com"
                  data-testid="input-email"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="title">Cargo</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Cargo o puesto"
                  data-testid="input-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Empresa</Label>
                  <Select 
                    value={formData.empresa_id} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="select-empresa">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {formOptions.empresas.map(e => (
                        <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Site</Label>
                  <Select 
                    value={formData.site_id} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="select-site">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {formOptions.sites.map(s => (
                        <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Área / Departamento</Label>
                <Select 
                  value={formData.location_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger data-testid="select-ubicacion">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {formOptions.ubicaciones.map(u => (
                      <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} data-testid="save-btn">
                {editingItem ? 'Guardar Cambios' : 'Crear Usuario'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Confirmar Eliminación</DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              ¿Estás seguro de que deseas eliminar al usuario <strong>{deleteConfirm?.name}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-btn">
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Usuarios;
